import EditIcon from "@mui/icons-material/Edit";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PrintIcon from "@mui/icons-material/Print";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState, useEffect, useMemo, JSX } from "react";
import { Box, Typography, Button, Alert, Skeleton, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import EquipmentForm from "../../components/ui/equipmentForm/equipmentForm.component";
import JobForm from "../../components/service/jobForm.component";
import JobStatusTransitionActionsModal, {
  JobStatusTransitionSaveDataPayload,
} from "../../components/service/jobStatusTransitionActionsModal.component";
import { useCRUD } from "../../api/crud";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";
import {
  JOBS_ENDPOINT,
  JOBS_STATUS_TRANSITION_ENDPOINT,
  JOB_NEXT_STATUSES_MAP,
  OWN_JOB_EDITABLE_STATUSES,
  OWN_JOB_STATUS_TRANSITION_STATUSES,
  getJobStatusLabelKey,
} from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useAuthContext, useHasPermission, useTenantName } from "../../contexts/auth";
import { Job, JobTask, StatusTransitionRequiredAction, StatusTransitionActionsRequiredError } from "../../types/jobs";
import { Equipment } from "../../types/equipments";
import { printJob } from "../../utils/printJob";

export const JobDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove, get } = useCRUD(JOBS_ENDPOINT);
  const { create: createStatusTransition, loading: statusTransitionLoading } =
    useCRUD(JOBS_STATUS_TRANSITION_ENDPOINT);
  const [updateJobOpened, setUpdateJobOpened] = useState<boolean>(false);
  const [updateTasksOpened, setUpdateTasksOpened] = useState<boolean>(false);
  const [complainJobOpened, setComplainJobOpened] = useState<boolean>(false);
  const [deleteJobOpened, setDeleteJobOpened] = useState<boolean>(false);
  const [changeAddressOpened, setChangeAddressOpened] = useState<boolean>(false);
  const [addressEquipmentId, setAddressEquipmentId] = useState<string | null>(null);
  const [transitionActionsOpened, setTransitionActionsOpened] = useState<boolean>(false);
  const [transitionRequiredActions, setTransitionRequiredActions] = useState<StatusTransitionRequiredAction[]>([]);
  const [pendingTransitionStatus, setPendingTransitionStatus] = useState<string | null>(null);
  const [transitionFieldErrors, setTransitionFieldErrors] = useState<Record<string, string[] | string> | undefined>(undefined);
  const [saveDataLoading, setSaveDataLoading] = useState<boolean>(false);
  const [jobLoading, setJobLoading] = useState<boolean>(true);
  const [job, setJob] = useState<Job | null>(null);
  const { currentUser } = useAuthContext();
  // Stands in for the installation address of equipment that is not out at a
  // client's site.
  const tenantName = useTenantName();
  const canChangeJobFull = useHasPermission("service.change_job");
  const canChangeOwnJob = useHasPermission("service.change_own_job");
  const canAddJob = useHasPermission("service.add_job");
  const canDeleteJobFull = useHasPermission("service.delete_job");
  const canDeleteOwnJob = useHasPermission("service.delete_own_job");
  const canChangeEquipmentAddress = useHasPermission("settlement.change_equipment");
  const isAssignedToCurrentUser = Boolean(
    currentUser && job?.assigned_to?.includes(currentUser.id)
  );
  const isOwnEditableStatus = Boolean(
    job?.status && OWN_JOB_EDITABLE_STATUSES.includes(job.status)
  );
  const isOwnStatusTransitionStatus = Boolean(
    job?.status && OWN_JOB_STATUS_TRANSITION_STATUSES.includes(job.status)
  );
  const canTransitionJobStatus =
    canChangeJobFull ||
    (canChangeOwnJob && isAssignedToCurrentUser && isOwnStatusTransitionStatus);
  const canDeleteJob =
    canDeleteJobFull || (canDeleteOwnJob && isAssignedToCurrentUser && isOwnEditableStatus);
  // Users without full job-edit rights (e.g. Serwisant) can still update the
  // tasks of jobs assigned to them, as long as the job is in an own-editable
  // status - mirrors the backend's own-job write permission check.
  const showUpdateTasks =
    !canChangeJobFull && canChangeOwnJob && isAssignedToCurrentUser && isOwnEditableStatus;
  const nextStatuses = job?.status ? JOB_NEXT_STATUSES_MAP[job.status] ?? [] : [];
  const showStatusTransitions = canTransitionJobStatus && nextStatuses.length > 0;
  const isJobFinished = job?.status === "F";
  const showComplainJob = canAddJob && isJobFinished;

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) {
        setJobLoading(false);
        return;
      }

      setJobLoading(true);
      const response = await get(id);
      if (response?.data) {
        setJob(response.data);
      }
      setJobLoading(false);
    };
    fetchJob();
    const handleJobUpdated = () => fetchJob();
    window.addEventListener("jobUpdated", handleJobUpdated);
    return () => window.removeEventListener("jobUpdated", handleJobUpdated);
  }, [id, get]);

  const handleDeleteJob = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Job deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteJobOpened(false);
      navigate('/service');
    } else {
      enqueueSnackbar(t('Failed to delete job'), ERROR_SNACKBAR);
    }
  };

  const handleStatusTransition = async (status: string) => {
    if (!id || statusTransitionLoading) {
      return;
    }

    const result = await createStatusTransition({ job_id: id, desired_status: status });

    if (result?.success) {
      enqueueSnackbar(t("Job status updated successfully"), SUCCESS_SNACKBAR);
      if (status === "F") {
        navigate("/service");
        return;
      }
      window.dispatchEvent(new Event("jobUpdated"));
      return;
    }

    const errorData = result?.errorData as StatusTransitionActionsRequiredError | undefined;
    if (errorData?.code === "job_status_transition_actions_required" && errorData.required_actions?.length) {
      setPendingTransitionStatus(status);
      setTransitionRequiredActions(errorData.required_actions);
      setTransitionFieldErrors(undefined);
      setTransitionActionsOpened(true);
      return;
    }

    const transitionErrorMessage =
      typeof result?.errorData === "string"
        ? result.errorData
        : result?.errorData?.message ??
          result?.errorData?.detail ??
          t("Failed to update job status");
    enqueueSnackbar(transitionErrorMessage, ERROR_SNACKBAR);
  };

  const handleTransitionWithActions = async (actionsData: Record<string, string>) => {
    if (!id || !pendingTransitionStatus || statusTransitionLoading) {
      return;
    }

    const result = await createStatusTransition({
      job_id: id,
      desired_status: pendingTransitionStatus,
      actions_data: actionsData,
    });

    if (result?.success) {
      enqueueSnackbar(t("Job status updated successfully"), SUCCESS_SNACKBAR);
      setTransitionActionsOpened(false);
      const wasFinishing = pendingTransitionStatus === "F";
      setPendingTransitionStatus(null);
      setTransitionFieldErrors(undefined);
      if (wasFinishing) {
        navigate("/service");
        return;
      }
      window.dispatchEvent(new Event("jobUpdated"));
      return;
    }

    const errorData = result?.errorData as StatusTransitionActionsRequiredError | undefined;
    if (errorData?.code === "job_status_transition_actions_required" && errorData.actions_data) {
      setTransitionFieldErrors(errorData.actions_data);
      return;
    }

    const errorMessage =
      typeof result?.errorData === "string"
        ? result.errorData
        : result?.errorData?.message ??
          result?.errorData?.detail ??
          t("Failed to update job status");
    enqueueSnackbar(errorMessage, ERROR_SNACKBAR);
    setTransitionActionsOpened(false);
  };

  const handleSaveTransitionData = async (payload: JobStatusTransitionSaveDataPayload) => {
    if (!id || saveDataLoading) {
      return false;
    }

    setSaveDataLoading(true);
    const result = await createStatusTransition({
      job_id: id,
      save_only: true,
      ...payload,
    });
    setSaveDataLoading(false);

    if (result?.success) {
      enqueueSnackbar(t("Progress saved successfully"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("jobUpdated"));
      return true;
    }

    const errorMessage =
      typeof result?.errorData === "string"
        ? result.errorData
        : result?.errorData?.message ??
          result?.errorData?.detail ??
          t("Failed to save changes");
    enqueueSnackbar(errorMessage, ERROR_SNACKBAR);
    return false;
  };

  const jobServiceEquipments = useMemo(() => {
    const jobTasks = job?.tasks ?? job?.services;
    if (!jobTasks) {
      return undefined;
    }

    const seen = new Set<number | string>();
    return jobTasks
      .map((s: JobTask) => s.equipment)
      .filter((eq): eq is Equipment => Boolean(eq?.id))
      .filter((eq) => {
        if (seen.has(eq.id)) return false;
        seen.add(eq.id);
        return true;
      });
  }, [job?.tasks, job?.services]);

  const handleOpenChangeAddress = () => {
    setAddressEquipmentId(
      jobServiceEquipments && jobServiceEquipments.length === 1 ? jobServiceEquipments[0].id : null
    );
    setChangeAddressOpened(true);
  };

  const jobStatusIconMap: Record<string, JSX.Element> = {
    IP: <PlayArrowIcon />,
    FV: <FactCheckIcon />,
    FI: <BuildIcon />,
    F: <CheckCircleIcon />,
  };

  const transitionActions: Action[] = nextStatuses.map((status) => {
    const statusLabel = t(getJobStatusLabelKey(status) ?? status).toLocaleLowerCase();

    return {
      icon: jobStatusIconMap[status] ?? <AutorenewIcon />,
      label: statusLabel,
      onClick: () => {
        handleStatusTransition(status);
      },
      disabled: statusTransitionLoading,
    };
  });

  const actions: Action[] = [
    ...(canChangeJobFull
      ? [
          {
            icon: <EditIcon />,
            label: t("Update job"),
            onClick: () => {
              setUpdateJobOpened(true);
            },
            disabled: isJobFinished,
          },
        ]
      : []),
    ...(showUpdateTasks
      ? [
          {
            icon: <ChecklistIcon />,
            label: t("Update tasks"),
            onClick: () => {
              setUpdateTasksOpened(true);
            },
          },
        ]
      : []),
    ...(showComplainJob
      ? [
          {
            icon: <ReportProblemIcon />,
            label: t("Complain job"),
            onClick: () => {
              setComplainJobOpened(true);
            },
          },
        ]
      : []),
    {
      icon: <LocationOnIcon />,
      label: t("Change installation address"),
      onClick: handleOpenChangeAddress,
      disabled: !canChangeEquipmentAddress || !jobServiceEquipments || jobServiceEquipments.length === 0,
    },
    {
      icon: <PrintIcon />,
      label: t("Print job"),
      onClick: () => {
        if (job) printJob(job, t, tenantName);
      },
      disabled: !job,
    },
    ...(canDeleteJob
      ? [
          {
            icon: <DeleteIcon />,
            label: t("Delete job"),
            onClick: () => {
              setDeleteJobOpened(true);
            },
            color: "var(--errorColor)",
          },
        ]
      : []),
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this job will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteJob}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {jobLoading ? (
          <>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                minWidth: 0,
                px: 1,
                pb: 1.5,
                mb: 2,
              }}
            >
              <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
            </Box>
            <Skeleton variant="rounded" height={44} />
          </>
        ) : (
          <>
            {showStatusTransitions && (
              <Box
                component="fieldset"
                sx={serviceActionsSectionStyle}
              >
                <Typography
                  component="legend"
                  variant="subtitle2"
                  sx={{
                    px: 1,
                    mb: 1,
                  }}
                >
                  {t("Change status")}
                </Typography>
                <Toolbar actions={transitionActions} noTopPadding showShortcut={false} />
              </Box>
            )}
            <Toolbar actions={actions} noTopPadding={showStatusTransitions} />
          </>
        )}
      </Box>
      <CustomModal
        opened={updateJobOpened}
        setOpened={setUpdateJobOpened}
        title={t("Update job")}
        content={
          <JobForm
            jobId={id}
            onSuccess={() => {
              setUpdateJobOpened(false);
              window.dispatchEvent(new Event("jobUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={updateTasksOpened}
        setOpened={setUpdateTasksOpened}
        title={t("Update tasks")}
        content={
          <JobForm
            jobId={id}
            onlyTasks
            onSuccess={() => {
              setUpdateTasksOpened(false);
              window.dispatchEvent(new Event("jobUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={complainJobOpened}
        setOpened={setComplainJobOpened}
        title={t("Complain job")}
        content={
          <JobForm
            initialComplaintForId={id}
            onSuccess={() => {
              setComplainJobOpened(false);
              navigate("/service/jobs");
            }}
          />
        }
      />
      <CustomModal
        opened={changeAddressOpened}
        setOpened={(value) => {
          setChangeAddressOpened(value);
          if (!value) {
            setAddressEquipmentId(null);
          }
        }}
        title={t("Change installation address")}
        content={
          addressEquipmentId ? (
            <EquipmentForm
              id={addressEquipmentId}
              onlyInstallationAddress
              onSuccess={() => {
                setChangeAddressOpened(false);
                setAddressEquipmentId(null);
                window.dispatchEvent(new Event("jobUpdated"));
              }}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("This job has multiple equipments. Select which one's installation address to change.")}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Equipment")}</InputLabel>
                <Select
                  label={t("Equipment")}
                  value=""
                  onChange={(e) => setAddressEquipmentId(e.target.value)}
                >
                  {(jobServiceEquipments ?? []).map((eq) => (
                    <MenuItem key={eq.id} value={eq.id}>
                      {eq.full_name || eq.serial || String(eq.id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )
        }
      />
      <CustomModal
        opened={deleteJobOpened}
        setOpened={setDeleteJobOpened}
        title={t("Delete job")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={transitionActionsOpened}
        setOpened={(value) => {
          setTransitionActionsOpened(value);
          if (!value) {
            setPendingTransitionStatus(null);
            setTransitionFieldErrors(undefined);
          }
        }}
        title={t("Additional actions required")}
        content={
          <JobStatusTransitionActionsModal
            jobId={id ?? ""}
            tasks={job?.tasks ?? job?.services ?? []}
            usedParts={job?.used_parts ?? []}
            initialRejectionReason={job?.rejection_reason}
            requiredActions={transitionRequiredActions}
            desiredStatus={pendingTransitionStatus}
            onSave={handleSaveTransitionData}
            saving={saveDataLoading}
            onConfirm={handleTransitionWithActions}
            onClose={() => {
              setTransitionActionsOpened(false);
              setPendingTransitionStatus(null);
              setTransitionFieldErrors(undefined);
            }}
            loading={statusTransitionLoading}
            fieldErrors={transitionFieldErrors}
          />
        }
      />
    </>
  );
};
